import { useEffect, useMemo, useState } from 'react';

import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type { DatasetVersion } from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';

export interface UseDatasetVersionsResult {
  versions: DatasetVersion[];
  isLoading: boolean;
  error: string | null;
}

export function useDatasetVersions(
  dataSetId: string,
): UseDatasetVersionsResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  const [versions, setVersions] = useState<DatasetVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    service
      .listDatasetVersions({
        dataSetId,
        pageSize: 100,
        includeCount: false,
      })
      .then((result) => {
        if (cancelled) return;
        setVersions(result.items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load versions',
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, dataSetId]);

  return { versions, isLoading, error };
}
